import { getAuthHeaders } from './network_calls';
import { GAMBIT_URL } from '../config/gambitEndpoint';

// The app's TypeScript DOM library predates the browser compression streams API.
declare const DecompressionStream: { new(format: 'gzip'): TransformStream<Uint8Array, Uint8Array> };

async function decodeWorkspaceResponse(data: any) {
  if (data?.gambitEncoding !== 'gzip-base64') return data;
  const bytes = Uint8Array.from(atob(data.payload), character => character.charCodeAt(0));
  const reader = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('gzip')).getReader();
  const decoder = new TextDecoder();
  const chunks: string[] = [];
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(decoder.decode(value, { stream: true }));
  }
  chunks.push(decoder.decode());
  return JSON.parse(chunks.join(''));
}

const sleep = (ms: number, signal: AbortSignal) => new Promise<void>((resolve, reject) => {
  const cancel = () => { clearTimeout(timer); reject(new DOMException('Request cancelled.', 'AbortError')); };
  const timer = setTimeout(() => { signal.removeEventListener('abort', cancel); resolve(); }, ms);
  if (signal.aborted) cancel(); else signal.addEventListener('abort', cancel, { once: true });
});
export async function gambitRequest(route: string, options: RequestInit = {}): Promise<any> {
  const controller = new AbortController();
  const cancel = () => controller.abort();
  if (options.signal?.aborted) cancel();
  options.signal?.addEventListener('abort', cancel, { once: true });
  // This waits for command acceptance, not generation. Cloud jobs keep running
  // independently of the tab, reconnects, and individual HTTP deadlines.
  const timeout = setTimeout(cancel, 120000);
  const rpc = async (body: Record<string, unknown>) => {
    const auth = await getAuthHeaders();
    if (!auth.Authorization) throw Object.assign(new Error('Sign in to continue.'), { status: 401, retryable: false });
    const response = await fetch(GAMBIT_URL, { method: 'POST', signal: controller.signal,
      headers: { ...auth, 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...body, ...(typeof DecompressionStream !== 'undefined' ? { responseEncoding: 'gzip-base64' } : {}) }) });
    const data = await decodeWorkspaceResponse(await response.json());
    if (!response.ok) throw Object.assign(new Error(data.message || 'Could not reach Gambit. Please retry.'),
      { status: response.status, retryable: response.status >= 500 || response.status === 429 });
    return data;
  };
  try {
    if (route === '/session') return await rpc({ operation: 'workspace-session' });
    const headers = new Headers(options.headers);
    const requestId = headers.get('Idempotency-Key') || crypto.randomUUID();
    const mutation = options.method === 'POST';
    const input = options.body ? JSON.parse(String(options.body)) : {};
    const envelope: Record<string, unknown> = { operation: mutation ? 'workspace-command' : 'workspace-read', route, requestId, input };
    if (JSON.stringify(envelope).length > 40000) {
      const upload = await rpc({ operation: 'workspace-upload' });
      const result = await fetch(upload.url, { method: 'PUT', signal: controller.signal,
        headers: { 'Content-Type': 'application/json', 'x-amz-server-side-encryption': 'AES256' }, body: JSON.stringify(input) });
      if (!result.ok) throw new Error('Could not upload the reference images. Please retry.');
      delete envelope.input; envelope.objectKey = upload.objectKey;
    }
    let result = await rpc(envelope);
    let pollDelay = 500;
    while (result.pending) {
      await sleep(pollDelay, controller.signal);
      result = await rpc({ operation: 'workspace-result', requestId });
      pollDelay = Math.min(pollDelay * 2, mutation ? 2000 : 4000);
    }
    if (result.error) throw Object.assign(new Error(result.error), { status: result.status, retryable: result.status >= 500 });
    return Object.hasOwn(result, 'data') ? result.data : result;
  } finally {
    clearTimeout(timeout); options.signal?.removeEventListener('abort', cancel);
  }
}

/** Keep media playing when a cloud refresh renews an otherwise identical URL. */
export function retainMediaUrl(previous: string | undefined, next: string): string {
  if (!previous) return next;
  try {
    const old = new URL(previous), current = new URL(next);
    if (old.origin !== current.origin || old.pathname !== current.pathname) return next;
    if (Number(old.searchParams.get('expires')) > Date.now() / 1000 + 60) return previous;
    const stamp = old.searchParams.get('X-Amz-Date');
    const expires = Number(old.searchParams.get('X-Amz-Expires'));
    if (stamp && /^\d{8}T\d{6}Z$/.test(stamp)) {
      const date = Date.UTC(+stamp.slice(0, 4), +stamp.slice(4, 6) - 1, +stamp.slice(6, 8), +stamp.slice(9, 11), +stamp.slice(11, 13), +stamp.slice(13, 15));
      if (date + expires * 1000 > Date.now() + 60000) return previous;
    }
  } catch { /* Keep the fresh URL if a previous value is malformed. */ }
  return next;
}

export function subscribeGambitEvents(options: {
  after: () => number; receive: (event: { data: string }) => void;
  resync: () => Promise<void>; error: (error: Error) => void;
  active?: () => boolean;
}) {
  const controller = new AbortController();
  let reading = false, failed = false, drain = true;
  let completedAt = 0;
  const poll = async () => {
    if (controller.signal.aborted || reading || failed || document.hidden) return;
    reading = true;
    drain = false;
    try {
      const page = await gambitRequest(`/events?after=${options.after()}`, { signal: controller.signal });
      if (controller.signal.aborted) return;
      for (const event of page.events || []) options.receive({ data: JSON.stringify(event) });
      if (page.cursor > options.after() && !page.events?.length) await options.resync();
      // Drain remaining events after a job finishes, then stop network reads
      // entirely while idle. Local work and returning to the tab wake us up.
      drain = !!page.events?.length;
      completedAt = Date.now();
    } catch (error) {
      failed = true;
      if (!controller.signal.aborted) options.error(error as Error);
    } finally { reading = false; }
  };
  const resume = () => {
    if (document.hidden || Date.now() - completedAt < 2000) return;
    drain = true;
    void poll();
  };
  // This lightweight local check notices commands/jobs without making idle
  // service calls or creating overlapping requests on slow connections.
  const timer = setInterval(() => {
    if ((drain || options.active?.()) && Date.now() - completedAt >= 2000) void poll();
  }, 2000);
  window.addEventListener('focus', resume);
  window.addEventListener('online', resume);
  document.addEventListener('visibilitychange', resume);
  void poll();
  return () => {
    clearInterval(timer);
    window.removeEventListener('focus', resume);
    window.removeEventListener('online', resume);
    document.removeEventListener('visibilitychange', resume);
    controller.abort();
  };
}
